import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  BarChart3,
  Users,
  Megaphone,
  Loader2,
  Inbox,
  RefreshCw,
  ChevronLeft,
  X as XIcon,
  Filter,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@easyflow/ui-frontend/components/ui/tabs';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Skeleton } from '@easyflow/ui-frontend/components/ui/skeleton-loader';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import { useSipPhone } from '@/contexts/SipPhoneContext';

import LeadTimelineModal from '@/components/crm/LeadTimelineModal';
import CampaignModal from '@/components/crm/CampaignModal';
import { MOBILE_BOTTOM_NAV_HEIGHT_PX } from '@/components/layout/MobileBottomNav';
import EmailComposeModal from '@/components/crm/EmailComposeModal';
import WhatsAppMiniChatModal from '@/components/messaging/WhatsAppMiniChatModal';

import LeadCard from '@/components/mobile/crm/LeadCard';
import CampaignCard from '@/components/mobile/crm/CampaignCard';
import MobileCRMDashboard, {
  type MobileDashboardData,
} from '@/components/mobile/crm/MobileCRMDashboard';
import { usePullToRefresh } from '@/components/mobile/crm/usePullToRefresh';
import type {
  MobileLead,
  MobileCampaign,
  MobilePipelineStage,
  CampaignDeps,
  CampaignFromModal,
  CampaignActivityStats,
} from '@/components/mobile/crm/types';

type TabId = 'dashboard' | 'leads' | 'campaigns';

interface MessagingSender {
  id: string;
  phone_number: string;
  friendly_name?: string;
  display_name?: string;
  channel_type?: 'sms' | 'whatsapp' | 'both' | string;
  is_default?: boolean;
}

const EMPTY_DEPS: CampaignDeps = {
  pipelineStages: [],
  automations: [],
  embedForms: [],
  phoneNumbers: [],
  chatbotAgents: [],
};

/**
 * Tries to unwrap responses that may either be returned directly OR wrapped in
 * `{ success, data }`. The api client lies about its return type — see
 * `apiClient.request` which forwards the parsed body as-is.
 */
function unwrap<T>(response: unknown, fallback: T): T {
  if (Array.isArray(response)) return response as unknown as T;
  if (response && typeof response === 'object') {
    const maybe = response as { data?: T };
    if (maybe.data !== undefined) return maybe.data;
    return response as T;
  }
  return fallback;
}

export default function MobileCRM() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const sipPhone = useSipPhone();
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');
  const [search, setSearch] = useState('');
  const scrollerRef = useRef<HTMLDivElement>(null);

  // Quick-action targets (Email / WhatsApp). Same in-app surfaces as desktop.
  const [emailTarget, setEmailTarget] = useState<{
    email: string;
    name: string;
    leadId?: string;
  } | null>(null);
  const [whatsAppTarget, setWhatsAppTarget] = useState<{
    leadId: string;
    phone: string;
    name: string;
  } | null>(null);

  // Leads
  const [leads, setLeads] = useState<MobileLead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsLoaded, setLeadsLoaded] = useState(false);
  const [stageFilter, setStageFilter] = useState<string | null>(null);
  const [stages, setStages] = useState<MobilePipelineStage[]>([]);
  const [stagesLoaded, setStagesLoaded] = useState(false);

  // Campaign drill-in filter for the Lead tab. When set, the Lead tab only
  // shows leads belonging to the selected campaign and a dismissable chip
  // appears above the list. This is what the user gets when they tap a
  // campaign card (the desktop "edit/settings" modal is NOT opened on mobile).
  const [campaignFilter, setCampaignFilter] = useState<{ id: string; name: string } | null>(null);

  // Campaigns
  const [campaigns, setCampaigns] = useState<MobileCampaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);

  // Activity stats per campaign id, harvested from the dashboard endpoint to
  // enrich Campagne cards with Contattati / Risposti / Convertiti.
  const [campaignStatsById, setCampaignStatsById] = useState<Record<string, CampaignActivityStats>>({});

  // Refresh trigger for the dashboard (pull-to-refresh).
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);

  // Lead detail modal
  const [openLead, setOpenLead] = useState<{ id: string; name: string } | null>(null);

  // Campaign modal + supporting data (lazy-loaded)
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [campaignDeps, setCampaignDeps] = useState<CampaignDeps>(EMPTY_DEPS);
  const [campaignDepsLoading, setCampaignDepsLoading] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<CampaignFromModal>(null);
  const [savingNewLead, setSavingNewLead] = useState(false);

  const loadStages = useCallback(async () => {
    try {
      const res = await apiClient.get<MobilePipelineStage[]>('/crm/pipeline-stages');
      const arr = unwrap<MobilePipelineStage[]>(res, []);
      setStages(Array.isArray(arr) ? arr : []);
    } catch {
      // non-fatal: just no chips
      setStages([]);
    } finally {
      setStagesLoaded(true);
    }
  }, []);

  const loadLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (stageFilter) params.append('stage_id', stageFilter);
      if (campaignFilter?.id) params.append('campaign_id', campaignFilter.id);
      const res = await apiClient.get<{ leads: MobileLead[]; total: number }>(
        `/crm/leads?${params.toString()}`,
      );
      const data = unwrap<{ leads?: MobileLead[] } | MobileLead[]>(res, []);
      const arr = Array.isArray(data)
        ? (data)
        : Array.isArray((data as { leads?: MobileLead[] }).leads)
          ? ((data as { leads: MobileLead[] }).leads)
          : [];
      setLeads(arr);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Impossibile caricare i lead';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
      setLeads([]);
    } finally {
      setLeadsLoading(false);
      setLeadsLoaded(true);
    }
  }, [toast, stageFilter, campaignFilter?.id]);

  const loadCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const res = await apiClient.get<MobileCampaign[]>(
        '/crm/campaigns?with_stats=true&include_excluded=true',
      );
      const arr = unwrap<MobileCampaign[]>(res, []);
      setCampaigns(Array.isArray(arr) ? arr : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Impossibile caricare le campagne';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
      setCampaigns([]);
    } finally {
      setCampaignsLoading(false);
      setCampaignsLoaded(true);
    }
  }, [toast]);

  const loadCampaignDeps = useCallback(async () => {
    if (campaignDepsLoading) return;
    if (
      campaignDeps.pipelineStages.length ||
      campaignDeps.automations.length ||
      campaignDeps.embedForms.length ||
      campaignDeps.phoneNumbers.length ||
      campaignDeps.chatbotAgents.length
    ) {
      return;
    }
    setCampaignDepsLoading(true);
    try {
      const [stagesRes, formsRes, autosRes, sendersRes, agentsRes] = await Promise.all([
        apiClient.get<CampaignDeps['pipelineStages']>('/crm/pipeline-stages').catch(() => null),
        apiClient.get<CampaignDeps['embedForms']>('/crm/forms').catch(() => null),
        apiClient.get<CampaignDeps['automations']>('/crm/automations').catch(() => null),
        apiClient
          .get<{ senders: MessagingSender[] } | MessagingSender[]>('/messaging/senders')
          .catch(() => null),
        apiClient.get<CampaignDeps['chatbotAgents']>('/crm/chatbot-agents').catch(() => null),
      ]);

      const sendersPayload = unwrap<{ senders: MessagingSender[] } | MessagingSender[]>(
        sendersRes,
        { senders: [] },
      );
      const senders: MessagingSender[] = Array.isArray(sendersPayload)
        ? sendersPayload
        : sendersPayload.senders ?? [];
      const phoneNumbers: CampaignDeps['phoneNumbers'] = senders.map((s) => ({
        id: s.id,
        phone_number: s.phone_number,
        friendly_name: s.friendly_name || s.display_name,
        type: s.channel_type === 'sms' ? 'sms' : s.channel_type === 'whatsapp' ? 'whatsapp' : 'both',
        is_default: !!s.is_default,
      }));

      setCampaignDeps({
        pipelineStages: unwrap<CampaignDeps['pipelineStages']>(stagesRes, []),
        automations: unwrap<CampaignDeps['automations']>(autosRes, []),
        embedForms: unwrap<CampaignDeps['embedForms']>(formsRes, []),
        phoneNumbers,
        chatbotAgents: unwrap<CampaignDeps['chatbotAgents']>(agentsRes, []),
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Impossibile caricare i dati campagna';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setCampaignDepsLoading(false);
    }
  }, [campaignDeps, campaignDepsLoading, toast]);

  // Initial / lazy data loads per active tab
  useEffect(() => {
    if (activeTab === 'leads' && !leadsLoaded && !leadsLoading) loadLeads();
    if (activeTab === 'campaigns' && !campaignsLoaded && !campaignsLoading) loadCampaigns();
    if (activeTab === 'leads' && !stagesLoaded) loadStages();
  }, [
    activeTab,
    leadsLoaded,
    leadsLoading,
    campaignsLoaded,
    campaignsLoading,
    stagesLoaded,
    loadLeads,
    loadCampaigns,
    loadStages,
  ]);

  // Re-fetch leads when either the stage filter OR the campaign drill-in
  // filter changes. We intentionally do NOT depend on `loadLeads` to avoid
  // double-firing on toast identity changes; loadLeads already depends on
  // both filters.
  useEffect(() => {
    if (activeTab === 'leads' && leadsLoaded) {
      void loadLeads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy guardrail #1219
  }, [stageFilter, campaignFilter?.id]);

  // Pull-to-refresh wired to the active tab
  const onPullRefresh = useCallback(async () => {
    if (activeTab === 'dashboard') {
      setDashboardRefreshKey((k) => k + 1);
    } else if (activeTab === 'leads') {
      await loadLeads();
    } else if (activeTab === 'campaigns') {
      await loadCampaigns();
      setDashboardRefreshKey((k) => k + 1); // keep activity stats fresh
    }
  }, [activeTab, loadLeads, loadCampaigns]);

  const pullState = usePullToRefresh(scrollerRef, { onRefresh: onPullRefresh });

  const handleDashboardData = useCallback((data: MobileDashboardData) => {
    const map: Record<string, CampaignActivityStats> = {};
    for (const c of data.activeCampaigns) {
      map[c.id] = c.stats;
    }
    setCampaignStatsById(map);
  }, []);

  const enrichedCampaigns = useMemo<MobileCampaign[]>(
    () =>
      campaigns.map((c) => ({
        ...c,
        activity_stats: campaignStatsById[c.id] ?? c.activity_stats,
      })),
    [campaigns, campaignStatsById],
  );

  const filteredLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = leads;
    if (!q) return base;
    return base.filter((l) => {
      const haystack = [
        l.contact_first_name,
        l.contact_last_name,
        l.company_name,
        l.contact_email,
        l.contact_phone,
        l.title,
        l.campaign_name,
        l.stage_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [leads, search]);

  const filteredCampaigns = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return enrichedCampaigns;
    return enrichedCampaigns.filter((c) =>
      [c.name, c.description, c.type, c.status].filter(Boolean).join(' ').toLowerCase().includes(q),
    );
  }, [enrichedCampaigns, search]);

  const handleLeadTap = useCallback((lead: MobileLead) => {
    const name =
      [lead.contact_first_name, lead.contact_last_name].filter(Boolean).join(' ').trim() ||
      lead.title ||
      'Lead';
    setOpenLead({ id: lead.id, name });
  }, []);

  // ---- Quick-action handlers (mirror desktop CRM) -----------------------
  // Call -> in-app SIP softphone (NOT `tel:`).
  // WhatsApp -> WhatsAppMiniChatModal (NOT `wa.me`).
  // Email -> EmailComposeModal (NOT `mailto:`).
  const handleQuickCall = useCallback(
    async (phone: string, name: string, _leadId: string) => {
      if (!phone) {
        toast({ title: 'Errore', description: 'Nessun numero di telefono disponibile', variant: 'destructive' });
        return;
      }
      if (!sipPhone) {
        toast({ title: 'Errore', description: 'Telefono SIP non disponibile', variant: 'destructive' });
        return;
      }
      const cleanNumber = phone
        .replace(/\s+/g, '')        // rimuovi spazi
        .replace(/^0039/, '')       // rimuovi 0039 iniziale
        .replace(/^\+39/, '')       // rimuovi +39 iniziale
        .replace(/^39(?=3)/, '');   // rimuovi 39 solo se seguito da 3 (cellulare italiano)
      try {
        sipPhone.openPhone();
        const success = await sipPhone.call(cleanNumber);
        if (success) {
          toast({ title: 'Chiamata in corso', description: `Chiamando ${name}…` });
        } else {
          toast({ title: 'Errore', description: 'Impossibile effettuare la chiamata', variant: 'destructive' });
        }
      } catch (error) {
        console.error('[MobileCRM] click-to-call error:', error);
        toast({ title: 'Errore', description: 'Errore durante la chiamata', variant: 'destructive' });
      }
    },
    [sipPhone, toast],
  );

  const handleQuickWhatsApp = useCallback((phone: string, name: string, leadId: string) => {
    setWhatsAppTarget({ leadId, phone, name });
  }, []);

  const handleQuickEmail = useCallback((email: string, name: string, leadId: string) => {
    setEmailTarget({ email, name, leadId });
  }, []);

  // ---- Back navigation --------------------------------------------------
  // From Lead/Campagne -> step back to the CRM Dashboard (so the user has
  // an obvious "indietro" inside the section). From Dashboard -> step back
  // in the router history; if there is none, fall through to the home.
  const handleBack = useCallback(() => {
    if (activeTab !== 'dashboard') {
      setActiveTab('dashboard');
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }, [activeTab, navigate]);

  // Tapping a campaign on mobile must show the LEADS inside that campaign,
  // NOT the desktop edit/settings modal (explicit user requirement). We set
  // the drill-in filter and switch to the Lead tab; the chip rendered at the
  // top of the list lets the user clear the filter and go back.
  // Editing a campaign on mobile is intentionally not exposed here — the
  // settings flow lives on desktop.
  const handleCampaignTap = useCallback((campaign: MobileCampaign) => {
    setCampaignFilter({ id: campaign.id, name: campaign.name || 'Campagna' });
    setStageFilter(null);
    setSearch('');
    setActiveTab('leads');
  }, []);

  const handleClearCampaignFilter = useCallback(() => {
    setCampaignFilter(null);
  }, []);

  const handleNewCampaign = useCallback(async () => {
    await loadCampaignDeps();
    setEditingCampaign(null);
    setCampaignModalOpen(true);
  }, [loadCampaignDeps]);

  const handleCampaignSaved = useCallback(() => {
    setCampaignModalOpen(false);
    setEditingCampaign(null);
    loadCampaigns();
    setDashboardRefreshKey((k) => k + 1);
  }, [loadCampaigns]);

  const handleNewLead = useCallback(async () => {
    // Quick-create a placeholder lead via API, then open the timeline modal so
    // the user can edit/expand it inside the existing detail UX. This avoids
    // duplicating the desktop New-Lead form on mobile.
    if (savingNewLead) return;
    setSavingNewLead(true);
    try {
      const res = await apiClient.post<MobileLead>('/crm/leads', {
        title: 'Nuovo lead',
        priority: 'medium',
        source: 'manual',
      });
      const created = unwrap<MobileLead>(res, null as unknown as MobileLead);
      if (created?.id) {
        setLeads((prev) => [created, ...prev]);
        setOpenLead({ id: created.id, name: created.title || 'Nuovo lead' });
      } else {
        await loadLeads();
        toast({ title: 'Lead creato', description: 'Apri la lista per modificarlo.' });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Impossibile creare il lead';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setSavingNewLead(false);
    }
  }, [savingNewLead, toast, loadLeads]);

  const searchPlaceholder =
    activeTab === 'leads'
      ? 'Cerca lead, contatto, azienda…'
      : activeTab === 'campaigns'
        ? 'Cerca campagne…'
        : 'Cerca lead o campagne…';

  // Visible pull translation while pulling/refreshing
  const pullTranslate = pullState.refreshing ? 48 : pullState.pullDistance;

  return (
    <div className="flex flex-col h-full -m-3 sm:-m-4 bg-background" data-testid="mobile-crm-root">
      {/* Sticky header: title + search + tabs */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="px-2 pt-3 pb-2 flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="h-11 w-11 shrink-0"
            aria-label={
              activeTab === 'dashboard' ? 'Torna alla schermata precedente' : 'Torna a Dashboard CRM'
            }
            data-testid="mobile-crm-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground truncate" data-testid="mobile-crm-title">
            CRM &amp; Campagne
          </h1>
        </div>
        {/* Compact search bar: always visible across all tabs (incl.
            Dashboard) so the user has a single, predictable surface to
            jump-search leads or campagne from anywhere in the section.
            Switching tabs preserves the current query. */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-9 h-11"
              data-testid="mobile-crm-search"
              inputMode="search"
            />
          </div>
        </div>

        {activeTab === 'leads' && stages.length > 0 && (
          <StageFilterChips
            stages={stages}
            value={stageFilter}
            onChange={setStageFilter}
          />
        )}

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)}>
          <TabsList className="w-full h-12 grid grid-cols-3 rounded-none bg-transparent border-t border-border p-0">
            <TabsTrigger
              value="dashboard"
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-primary h-full"
              data-testid="mobile-crm-tab-dashboard"
            >
              <BarChart3 className="w-4 h-4 mr-1.5" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger
              value="leads"
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-primary h-full"
              data-testid="mobile-crm-tab-leads"
            >
              <Users className="w-4 h-4 mr-1.5" />
              Lead
            </TabsTrigger>
            <TabsTrigger
              value="campaigns"
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-primary h-full"
              data-testid="mobile-crm-tab-campaigns"
            >
              <Megaphone className="w-4 h-4 mr-1.5" />
              Campagne
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Pull-to-refresh indicator */}
        {(pullState.pulling || pullState.refreshing) && (
          <div
            className="flex items-center justify-center text-xs text-muted-foreground gap-2 py-1"
            data-testid="mobile-crm-pull-indicator"
            aria-hidden="true"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${pullState.refreshing ? 'animate-spin' : ''}`}
              style={{
                transform: pullState.refreshing
                  ? undefined
                  : `rotate(${Math.min(pullState.pullDistance * 4, 360)}deg)`,
              }}
            />
            {pullState.refreshing ? 'Aggiorno…' : pullState.pullDistance > 60 ? 'Rilascia' : 'Tira giù'}
          </div>
        )}
      </div>

      {/* Tab content (scroll container + pull-to-refresh anchor) */}
      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto overscroll-contain"
        style={{
          paddingBottom: `calc(env(safe-area-inset-bottom) + 88px)`,
          transform: pullTranslate ? `translateY(${pullTranslate}px)` : undefined,
          transition: pullState.pulling ? 'none' : 'transform 200ms ease',
        }}
      >
        {/* Tabs is also rendered in the header, but we still need `TabsContent`
            children inside a `Tabs` root for state. We use a single Tabs root
            wrapping the content area. */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)}>
          <TabsContent value="dashboard" className="m-0 p-3" data-testid="mobile-crm-dashboard">
            <MobileCRMDashboard
              refreshKey={dashboardRefreshKey}
              onData={handleDashboardData}
              onActivityTap={(leadId) => setOpenLead({ id: leadId, name: 'Lead' })}
            />
          </TabsContent>

          <TabsContent value="leads" className="m-0 p-3 space-y-2" data-testid="mobile-crm-leads">
            {/* Campaign drill-in chip: appears only when the user landed on
                the Lead tab by tapping a campaign card. Tap "X" to clear. */}
            {campaignFilter && (
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/20"
                data-testid="mobile-crm-campaign-filter"
              >
                <Filter className="w-4 h-4 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-wide text-primary/70 font-medium leading-tight">
                    Campagna
                  </p>
                  <p className="text-sm font-medium text-foreground truncate leading-tight">
                    {campaignFilter.name}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleClearCampaignFilter}
                  className="w-9 h-9 -mr-1 rounded-full flex items-center justify-center text-primary hover:bg-primary/10 active:bg-primary/20 transition-colors"
                  aria-label="Rimuovi filtro campagna"
                  data-testid="mobile-crm-campaign-filter-clear"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
            )}

            {leadsLoading && !leadsLoaded ? (
              <LeadsSkeleton />
            ) : filteredLeads.length === 0 ? (
              <EmptyState
                icon={<Users className="w-10 h-10 text-muted-foreground" />}
                title={
                  search || stageFilter || campaignFilter
                    ? 'Nessun lead trovato'
                    : 'Ancora nessun lead'
                }
                description={
                  search
                    ? 'Prova a cambiare i termini di ricerca.'
                    : campaignFilter
                      ? `Nessun lead nella campagna "${campaignFilter.name}".`
                      : stageFilter
                        ? 'Nessun lead in questo stage. Tocca "Tutti" per rimuovere il filtro.'
                        : 'Tocca + per creare il tuo primo lead.'
                }
              />
            ) : (
              <>
                <p className="text-xs text-muted-foreground px-1 py-1" data-testid="mobile-crm-leads-count">
                  {filteredLeads.length} lead
                  {campaignFilter ? ' in questa campagna' : ''}
                </p>
                {filteredLeads.map((l) => (
                  <LeadCard
                    key={l.id}
                    lead={l}
                    onTap={handleLeadTap}
                    onCall={handleQuickCall}
                    onWhatsApp={handleQuickWhatsApp}
                    onEmail={handleQuickEmail}
                  />
                ))}
              </>
            )}
          </TabsContent>

          <TabsContent
            value="campaigns"
            className="m-0 p-3 space-y-2"
            data-testid="mobile-crm-campaigns"
          >
            {campaignsLoading && !campaignsLoaded ? (
              <CampaignsSkeleton />
            ) : filteredCampaigns.length === 0 ? (
              <EmptyState
                icon={<Megaphone className="w-10 h-10 text-muted-foreground" />}
                title={search ? 'Nessuna campagna trovata' : 'Ancora nessuna campagna'}
                description={
                  search
                    ? 'Prova a cambiare i termini di ricerca.'
                    : 'Tocca + per creare la tua prima campagna.'
                }
              />
            ) : (
              <>
                <p className="text-xs text-muted-foreground px-1 py-1" data-testid="mobile-crm-campaigns-count">
                  {filteredCampaigns.length} campagne
                </p>
                {filteredCampaigns.map((c) => (
                  <CampaignCard key={c.id} campaign={c} onTap={handleCampaignTap} />
                ))}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* FAB: Nuovo lead on Lead tab, Nuova campagna on Campagne tab */}
      {activeTab === 'leads' && (
        <div
          className="fixed right-4 z-30"
          style={{ bottom: `calc(env(safe-area-inset-bottom) + ${MOBILE_BOTTOM_NAV_HEIGHT_PX + 24}px)` }}
        >
          <Button
            size="lg"
            className="h-14 w-14 rounded-full shadow-lg p-0"
            onClick={handleNewLead}
            disabled={savingNewLead}
            data-testid="mobile-crm-fab-new-lead"
            aria-label="Nuovo lead"
          >
            {savingNewLead ? <Loader2 className="w-6 h-6 animate-spin" /> : <Plus className="w-6 h-6" />}
          </Button>
        </div>
      )}
      {activeTab === 'campaigns' && (
        <div
          className="fixed right-4 z-30"
          style={{ bottom: `calc(env(safe-area-inset-bottom) + ${MOBILE_BOTTOM_NAV_HEIGHT_PX + 24}px)` }}
        >
          <Button
            size="lg"
            className="h-14 w-14 rounded-full shadow-lg p-0"
            onClick={handleNewCampaign}
            disabled={campaignDepsLoading}
            data-testid="mobile-crm-fab-new-campaign"
            aria-label="Nuova campagna"
          >
            {campaignDepsLoading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <Plus className="w-6 h-6" />
            )}
          </Button>
        </div>
      )}

      {/* Lead detail */}
      {openLead && (
        <LeadTimelineModal
          open={!!openLead}
          onOpenChange={(o) => !o && setOpenLead(null)}
          leadId={openLead.id}
          leadName={openLead.name}
        />
      )}

      {/* Campaign create/edit */}
      <CampaignModal
        open={campaignModalOpen}
        onOpenChange={(o) => {
          setCampaignModalOpen(o);
          if (!o) setEditingCampaign(null);
        }}
        campaign={editingCampaign}
        onSave={handleCampaignSaved}
        pipelineStages={campaignDeps.pipelineStages}
        automations={campaignDeps.automations}
        embedForms={campaignDeps.embedForms}
        phoneNumbers={campaignDeps.phoneNumbers}
        chatbotAgents={campaignDeps.chatbotAgents}
      />

      {/* In-app WhatsApp quick chat (same component as desktop CRM). */}
      {whatsAppTarget && (
        <WhatsAppMiniChatModal
          open={!!whatsAppTarget}
          onClose={() => setWhatsAppTarget(null)}
          leadId={whatsAppTarget.leadId}
          leadPhone={whatsAppTarget.phone}
          leadName={whatsAppTarget.name}
        />
      )}

      {/* In-app email composer (same component as desktop CRM). */}
      <EmailComposeModal
        open={!!emailTarget}
        onClose={() => setEmailTarget(null)}
        mode="compose"
        defaultTo={emailTarget?.email || ''}
        defaultName={emailTarget?.name || ''}
        leadId={emailTarget?.leadId}
        onSent={() => setEmailTarget(null)}
      />
    </div>
  );
}

function StageFilterChips({
  stages,
  value,
  onChange,
}: {
  stages: MobilePipelineStage[];
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const sorted = useMemo(
    () => stages.slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [stages],
  );
  return (
    <div
      className="flex gap-2 overflow-x-auto px-4 pb-2 no-scrollbar"
      data-testid="mobile-crm-stage-filter"
    >
      <Chip active={value === null} onClick={() => onChange(null)} data-testid="mobile-crm-stage-all">
        Tutti
      </Chip>
      {sorted.map((s) => (
        <Chip
          key={s.id}
          active={value === s.id}
          color={s.color}
          onClick={() => onChange(s.id)}
          data-testid={`mobile-crm-stage-${s.id}`}
        >
          {s.name}
        </Chip>
      ))}
    </div>
  );
}

function Chip({
  children,
  active,
  color,
  onClick,
  ...rest
}: {
  children: React.ReactNode;
  active?: boolean;
  color?: string;
  onClick: () => void;
  'data-testid'?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 min-h-[36px] px-3 rounded-full text-xs font-medium border transition-colors ${
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-card text-foreground border-border hover:bg-accent'
      }`}
      // When the active stage carries a brand colour, override the surface
      // (background+border) with the stage colour while letting the foreground
      // come from the `text-primary-foreground` Tailwind token applied above.
      style={
        active && color
          ? { backgroundColor: color, borderColor: color }
          : undefined
      }
      data-testid={rest['data-testid']}
    >
      {children}
    </button>
  );
}

function EmptyState({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6" data-testid="mobile-crm-empty">
      <div className="mb-4 p-4 rounded-full bg-muted">
        <Inbox className="w-10 h-10 text-muted-foreground" />
        <span className="sr-only">{icon}</span>
      </div>
      <h3 className="font-semibold text-foreground mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground max-w-xs">{description}</p>
    </div>
  );
}

function LeadsSkeleton() {
  return (
    <div className="space-y-2" data-testid="mobile-crm-leads-skeleton">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-card p-4 space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

function CampaignsSkeleton() {
  return (
    <div className="space-y-2" data-testid="mobile-crm-campaigns-skeleton">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-card p-4 flex gap-3">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}
